import { BASE_FONT_SIZE_PX } from '../../constants/general';

const isNonNullable = <T>(thing: T): thing is NonNullable<T> => {
	return !!thing;
};

/**
 * Need to remove a branch from your code?  Let me do it for you!
 * This might be helpful if you have a branch which would be difficult
 * to test, i.e. `useRef` result which is nullable.
 *
 * @param thing - the thing to check
 * @param action - callback with thing typed without null/undefined
 */
export const doIfDefined = <T>(thing: T, action: (thing: NonNullable<T>) => void) => {
	if (isNonNullable(thing)) {
		action(thing);
	}
};

/**
 * Converts pixel value to rem assuming a 16px root font.
 * Used in cases when you want to use native api to get pixel values to do
 * calculations with and then set style attribute in rem.
 *
 * @param px
 */
export const pxToRem = (px: number): string => `${px / BASE_FONT_SIZE_PX}rem`;

const numberFormatter = new Intl.NumberFormat();

/**
 * Format number with commas
 *
 * @param value number to format
 */
export const formatNumber = (value: number) => numberFormatter.format(value);

const priceFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/**
 * Format number as US dollar
 *
 * @param value dollar amount as a number
 */
export const formatPrice = (value: number | string): string => {
	if (typeof value === 'string') {
		value = parseFloat(value);
	}
	return priceFormatter.format(value);
};

const percentFormatter = new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 2 });

export const formatPercent = (value: number | string, returnOnInvalid = ''): string => {
	if (typeof value === 'string') {
		value = parseFloat(value);
	}

	if (!isFinite(value)) {
		return returnOnInvalid;
	}

	const percent = percentFormatter.format(value / 100);

	// This is to maintain two decimal places when number is not a
	// decimal such as "0.00%". Without this "0%" would be returned.
	return `${(Math.round(parseFloat(percent) * 100) / 100).toFixed(2)}%`;
};

export function escapeRegex(pattern: string) {
	// REGEX: escape special characters in regex.
	// Source: https://makandracards.com/makandra/15879-javascript-how-to-generate-a-regular-expression-from-a-string
	return pattern.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
}

// REGEX: looks for 5 digits with `-` followed by 4(#####-####) or 5 digits or 9
const validZipCodeRegex = /^(\d{5}-\d{4}|\d{5}|\d{9})$/;

// REGEX: Canadian postal code such as M3C 1R7 (or M3C1R7 without space)
const validCanadaZipCodeRegex = /^([A-Z]\d[A-Z] ?\d[A-Z]\d)$/;

export function isZipCodeValid(zipCode, usAndCa = false) {
	return Boolean(
		zipCode && usAndCa ? validCanadaZipCodeRegex.test(zipCode) || validZipCodeRegex.test(zipCode) : validZipCodeRegex.test(zipCode)
	);
}

export const makeUnique = (list: any[]): any[] => {
	return list.reduce((result, current) => {
		return result.includes(current) ? result : [...result, current];
	}, []);
};

/**
 * Parses section values and runs validations against it. If the value is not considered valid, this method returns 0.
 *
 * @param {string} name - the name of the field
 * @param {string} value - the value from the field
 * @returns {object}
 */
export const parseValue = (name: string, value: string): object => {
	const parsedValue = parseInt(value, 10);
	return { [`${name}`]: Number.isNaN(parsedValue) ? null : parsedValue };
};

/**
 * Pluralizes a given word.
 * By default this method adds a "s" character at the end of the given word.
 * If there is a special case i.e: tomato -> tomato"es", add the word into the dictionary map (singular, plural) to handle these cases
 * on an on-demand basis.
 *
 * Important Note: Candidate to move into fergy-utilities package.
 *
 * @param {string} word
 * @param {number} quantity
 * @param {{ singular: string, plural: string }[]} [dictionary]
 * @returns {string}
 */
const pluralizationDictionary: { singular: string; plural: string }[] = [];
export const pluralize = (word: string, quantity: number, dictionary = pluralizationDictionary): string => {
	const specialWord = dictionary.find((dWord) => dWord.singular === word.toLowerCase());
	const pluralSpecialWord = specialWord ? specialWord.plural : `${word}s`;
	return quantity !== 1 ? pluralSpecialWord : word;
};

type ComponentType =
	| 'link'
	| 'button'
	| 'input'
	| 'checkbox'
	| 'select'
	| 'textarea'
	| 'facet'
	| 'breadcrumb'
	| 'toggle'
	| 'listitem'
	| 'radiogroup'
	| 'panel'
	| 'tab'
	| 'section';
/**
 * Generates a data selector id
 *
 * @param {string} componentType - the type of component the id belongs to
 * @param {string} id - automation/analytics id
 */
export const generateDataSelector = (componentType: ComponentType, id: string | undefined): string | undefined => {
	return id ? `${id.split(' ').join('-').toLowerCase()}-${componentType}` : undefined;
};

/*
 * Configuration for building a single query string parameter.
 * Key and value represent the usual parts of a query string parameter.
 * If condition is defined and is false, then the parameter will be skipped when building the query string.
 * If encode is false, the parameter will not be URI encoded.
 */
type QueryStringBuilderParam = {
	key: string;
	value: any;
	condition?: boolean;
	encode?: boolean;
};

/**
 * Builder for constructing a query string from parts.
 */
export class QueryStringBuilder {
	private config: QueryStringBuilderParam[] = [];

	/**
	 * Construct a query string from the parts added to the builder.
	 */
	build(): string {
		const params = this.config.reduce<string[]>((result, { key, value, condition = true, encode = true }) => {
			if (key && value !== undefined && value !== null && condition) {
				result.push(`${encode ? encodeURIComponent(key) : key}=${encode ? encodeURIComponent(value) : value}`);
			}
			return result;
		}, []);
		return params.join('&');
	}

	/**
	 * Add a new parameter to the end of the builder.
	 *
	 * @param key Parameter key. Parameter will be ignored if empty.
	 * @param value Parameter value. Will be converted to a string value when building the query string.
	 * Parameter will be ignored if null or undefined.
	 * @param condition If true, include this parameter when building the query string, otherwise ignore it. Default: true
	 * @param encode If true, URI encode the key and value. Default: true
	 */
	push(key: string, value: any, condition?: boolean, encode?: boolean): QueryStringBuilder {
		this.config.push({ key, value, condition, encode });
		return this;
	}
}

export const booleanFromQueryString = (qsValue?: string | null): boolean => {
	const validValueMap = {
		'0': false,
		'1': true,
		false: false,
		true: true
	};
	return Boolean(validValueMap[qsValue ?? '']);
};

/**
 * Converts a number to two decimal places
 */
export const toDecimal = (amount: number): number => {
	return parseFloat(amount.toFixed(2));
};

/**
 * Sets all the values on an object to the passed in value
 */

export function setAllObjectValues<T extends object, K extends keyof T>(obj: T, value: T[K]): T {
	const newObj = { ...obj };

	Object.keys(newObj).forEach((k) => {
		newObj[k] = value;
	});

	return newObj;
}

/**
 * Validates a youtube URL
 */
export const validateYoutubeUrl = (url: string): boolean => {
	if (url) {
		const regExp =
			/^(?:https?:\/\/)?(?:m\.|www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;
		if (url.match(regExp)) {
			return true;
		}
	}
	return false;
};

/**
 * Chunks an array into arrays of size `step`
 * Example: chunkEvery([1, 2, 3, 4, 5], 2) becomes [[1, 2], [3, 4], [5]]
 */
export const chunkEvery = <T>(array: Array<T>, step: number): Array<Array<T>> => {
	if (step < 1) {
		return [];
	}

	const chunker = <T>(xs: Array<T>, result: Array<Array<T>>): Array<Array<T>> => {
		if (xs.length <= step) {
			return result.concat([xs]);
		} else {
			return chunker(xs.slice(step), result.concat([xs.slice(0, step)]));
		}
	};

	return chunker<T>(array, []);
};

/**
 * Formats a boolean to a 'yes' or 'no' string
 */
export const formatBoolean = (bool: boolean) => (bool ? 'Yes' : 'No');

export type YesOrNo = 'yes' | 'no';
/**
 * Converts a 'yes' or 'no' string to a boolean
 *
 * @param val - 'yes' or 'no'
 * @returns true for 'yes', false for 'no'
 */
export const boolFromYesNo = (val: YesOrNo): boolean => val === 'yes';
